<template>
  <!-- Слайдер выгод корзины: минимум заказа, бесплатная доставка и оба подарка
       (за сумму и за частоту) лежат в одной ленте, а не разными блоками по корзине.

       Порядок ленты живой: невыполненные цели держим в начале, выполненные уезжают
       в хвост. Гость видит, до чего ещё можно дотянуться, а не листает карточки,
       которые уже отработали. -->
  <div v-if="cards.length" class="mb-8">
    <ClientOnly>
      <swiper-container
        ref="swiperElement"
        :space-between="8"
        :slides-per-view="1.25"
        class="flex"
      >
        <swiper-slide
          v-for="card in cards"
          :key="card.key"
          :data-testid="`benefit-${card.key}`"
          class="flex h-auto py-3.5"
        >
          <!-- Карточка до минимальной суммы заказа -->
          <div
            v-if="card.key === 'minimum'"
            class="info-card"
            :style="{ '--benefit-tone': BENEFIT_TONES.minimum }"
          >
            <BasketGoalRing :percent="minimumPercent" :tone="BENEFIT_TONES.minimum">
              ⚡
            </BasketGoalRing>
            <div class="info-card__content">
              <div class="info-card__title">
                <span v-if="toMinOrder > 0">Ещё {{ toMinOrder }} ₽</span>
                <span v-else>Минимум достигнут</span>
              </div>
              <div class="info-card__subtitle">
                до минимальной суммы корзины
              </div>
            </div>
          </div>

          <!-- Карточка до бесплатной доставки -->
          <div
            v-else-if="card.key === 'delivery'"
            class="info-card"
            :style="{ '--benefit-tone': BENEFIT_TONES.delivery }"
          >
            <BasketGoalRing :percent="deliveryPercent" :tone="BENEFIT_TONES.delivery">
              🚚
            </BasketGoalRing>
            <div class="info-card__content">
              <div class="info-card__title">
                <span v-if="toFreeDelivery && toFreeDelivery > 0">Ещё {{ toFreeDelivery }} ₽</span>
                <span v-else>Доставка бесплатна</span>
              </div>
              <div class="info-card__subtitle">
                до бесплатной доставки
              </div>
            </div>
          </div>

          <!-- Карточка если бесплатная доставка недоступна -->
          <div
            v-else-if="card.key === 'delivery-none'"
            class="info-card info-card--unavailable"
            :style="{ '--benefit-tone': BENEFIT_TONES.unavailable }"
          >
            <!-- Цели нет — кольцо пустое, оно здесь только ради общей геометрии ленты. -->
            <BasketGoalRing :percent="0" :tone="BENEFIT_TONES.unavailable">
              🚚
            </BasketGoalRing>
            <div class="info-card__content">
              <div class="info-card__title">
                Бесплатной доставки нет
              </div>
              <div class="info-card__subtitle">
                в этой зоне доставка всегда платная
              </div>
            </div>
          </div>

          <!-- Подарок за сумму заказа (рост среднего чека) -->
          <BasketGiftTierNudge v-else-if="card.key === 'gift-tier'" />

          <!-- Подарок за частоту заказов. Механика независима от подарка за сумму,
               поэтому карточка отдельная; при пересечении гость выбирает один подарок. -->
          <BasketWeeklyGift
            v-else-if="card.key === 'weekly-gift'"
            :gift-tier-also-available="isGiftTierGranted"
          />
        </swiper-slide>
      </swiper-container>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, unref, watch } from 'vue'
import BasketGiftTierNudge from './BasketGiftTierNudge.vue'
import BasketGoalRing from './BasketGoalRing.vue'
import BasketWeeklyGift from './BasketWeeklyGift.vue'
import { BENEFIT_TONES } from './benefitTones'
import { useGiftTiers, useWeeklyGift } from '@features/basket'
import { useInstitutionStore } from '@entities/institution'

const props = defineProps<{
  cartAmount: number, // сумма товаров в корзине (без доставки)
  minOrderAmount: number,
  deliveryRules: Array<{
    min_order_amount: number,
    max_order_amount: number | null,
    delivery_cost: number
  }>,
  /** Самовывоз: минимум корзины и бесплатная доставка к заказу не применяются. */
  isDelivery: boolean
}>()

// ✅ Безопасное получение суммы корзины с unwrap'ом для SSR
const safeCartAmount = computed(() => {
  const amount = unref(props.cartAmount)
  return typeof amount === 'number' ? amount : 0
})

// ✅ Безопасное получение минимальной суммы
const safeMinOrderAmount = computed(() => {
  const amount = unref(props.minOrderAmount)
  return typeof amount === 'number' ? amount : 0
})

// Сколько не хватает до минимального заказа
const toMinOrder = computed(() => 
  Math.max(safeMinOrderAmount.value - safeCartAmount.value, 0),
)

// Правило бесплатной доставки (если есть)
const freeRule = computed(() => {
  const rules = unref(props.deliveryRules)
  return Array.isArray(rules)
    ? rules.find(rule => rule.delivery_cost === 0)
    : null
})

// Сколько не хватает до бесплатной доставки, либо null если её не бывает
const toFreeDelivery = computed(() => {
  if (!freeRule.value) return null
  return Math.max((freeRule.value.min_order_amount || 0) - safeCartAmount.value, 0)
})

// ============================================
// ⭕ Кольца прогресса
// ============================================
/** Доля пути до порога в процентах. Порог 0 (или мусор в данных) — цель уже взята. */
function toPercent(amount: number, threshold: number): number {
  if (!Number.isFinite(threshold) || threshold <= 0) return 100
  return Math.min(100, Math.round((amount / threshold) * 100))
}

const minimumPercent = computed(() => toPercent(safeCartAmount.value, safeMinOrderAmount.value))

const deliveryPercent = computed(() =>
  toPercent(safeCartAmount.value, freeRule.value?.min_order_amount ?? 0),
)

// ============================================
// 🎁 Подарочные карточки слайдера
// ============================================
// Видимость подарков считаем здесь, а не внутри самих компонентов: пустой
// swiper-slide остался бы дыркой в ленте, поэтому слайд не должен рождаться вовсе.
const { nudge, grantedGiftProductId } = useGiftTiers()
const {
  shouldShow: weeklyShouldShow,
  progress: weeklyProgress,
  isGiftInCart: isWeeklyGiftInCart,
  load: loadWeeklyGift,
} = useWeeklyGift()
const institution = useInstitutionStore()

const hasGiftTierNudge = computed(() => Boolean(nudge.value))

// Подарок за сумму уже лежит в корзине — карточка «за частоту» предупредит,
// что забрать можно только один подарок.
const isGiftTierGranted = computed(() => Boolean(grantedGiftProductId.value))

// ============================================
// 🔀 Порядок ленты
// ============================================
/** Слайд ленты: `done` — цель закрыта, дотягиваться больше не до чего. */
interface InfoCard {
  key: 'minimum' | 'delivery' | 'delivery-none' | 'gift-tier' | 'weekly-gift',
  done: boolean
}

const cards = computed<InfoCard[]>(() => {
  const list: InfoCard[] = []

  if (props.isDelivery) {
    list.push(
      { key: 'minimum', done: toMinOrder.value <= 0 },
      // Бесплатной доставки в зоне не бывает — цели нет вовсе, карточка справочная,
      // поэтому она сразу считается закрытой и живёт в хвосте.
      toFreeDelivery.value === null
        ? { key: 'delivery-none', done: true }
        : { key: 'delivery', done: toFreeDelivery.value <= 0 },
    )
  }

  // Подарок за сумму закрыт только на максимальном пороге: пока есть следующий шаг,
  // цель живая, даже если текущий подарок уже лежит в корзине.
  if (hasGiftTierNudge.value) {
    list.push({ key: 'gift-tier', done: !nudge.value?.hasNext })
  }

  // Подарок за частоту закрыт, когда он уже в корзине. Пока условие выполнено,
  // но подарок не забран, у гостя остаётся действие — карточка держится в начале.
  if (weeklyShouldShow.value) {
    list.push({
      key: 'weekly-gift',
      done: weeklyProgress.value.available && isWeeklyGiftInCart.value,
    })
  }

  // Сортировка стабильная: внутри групп порядок объявления сохраняется,
  // и лента не перетасовывается на каждый пересчёт корзины.
  return [...list.filter(card => !card.done), ...list.filter(card => card.done)]
})

// Порядок сменился — возвращаем ленту к началу. Иначе гость остаётся на карточке,
// которая только что уехала в хвост, и первую невыполненную цель не видит.
const swiperElement = ref<{ swiper?: { update: () => void, slideTo: (index: number) => void } } | null>(null)

watch(
  () => cards.value.map(card => card.key).join('|'),
  async () => {
    await nextTick()
    const swiper = swiperElement.value?.swiper
    if (!swiper) return

    // Слайды переставил Vue — Swiper пересчитывает геометрию только по своей команде.
    swiper.update()
    swiper.slideTo(0)
  },
)

// Прогресс подарка за частоту грузим здесь: карточка ниже показывается только по
// его результату, а значит сама себя загрузить уже не сможет — не смонтируется.
onMounted(loadWeeklyGift)

// Прогресс зависит от филиала (своё меню и свой подарок) — перечитываем при смене.
watch(() => institution.selectedBranch?.id, () => void loadWeeklyGift())
</script>

<style scoped lang="scss">
// ============================================
// 🎴 INFO CARD - Информационные карточки
// ============================================

.info-card {
  @apply relative flex items-center gap-3 w-full px-4 py-3 overflow-hidden;
  // Скругление из пресета «Скругления элементов»
  border-radius: var(--app-radius-card, 24px);
  @apply transition-all duration-300 ease-out;
  @apply shadow-sm hover:shadow-md;
  @apply hover:-translate-y-1 hover:shadow-lg;
  @apply active:translate-y-0 active:scale-95 active:duration-150;

  // Фон и рамка целиком выведены из тона карточки (--benefit-tone, см. benefitTones.ts).
  // Цвет закреплён за механикой и НЕ меняется при достижении цели: иначе «минимум
  // достигнут» и «доставка бесплатна» зеленели оба и переставали различаться.
  background: linear-gradient(
    to bottom right,
    rgb(var(--benefit-tone) / 0.14),
    rgb(var(--benefit-tone) / 0.05)
  );
  border: 1px solid rgb(var(--benefit-tone) / 0.3);
  animation: benefitGlow 3s ease-in-out infinite;

  // Справочная карточка «бесплатной доставки нет» цели не несёт — не мигаем.
  &--unavailable {
    animation: none;
  }

  // ============================================
  // 🎨 Элементы карточки
  // ============================================

  // Кольцо прогресса — отдельный компонент (BasketGoalRing), поэтому подъём
  // иконки на hover достаём до его вложенной вёрстки.
  &:hover :deep(.goal-ring__badge) {
    @apply scale-110 rotate-3;
  }

  &__content {
    @apply flex flex-col flex-1 min-w-0;
  }

  &__title {
    @apply font-bold text-sm tracking-tight;
    @apply text-gray-900 dark:text-white;
    @apply tabular-nums leading-tight;
    @apply md:text-xs;

    @media (max-width: 768px) {
      @apply text-xs;
    }
  }

  &__subtitle {
    @apply text-xs tracking-wide;
    @apply text-gray-600 dark:text-gray-400;
    @apply leading-tight;

    @media (max-width: 768px) {
      font-size: 0.65rem;
    }
  }
}

// ============================================
// 🎬 ANIMATIONS - Анимации для привлечения внимания
// ============================================

// Мягкое свечение в СОБСТВЕННОМ тоне карточки: раньше свечение было общим
// (оранжевый + синий на всех), из-за чего лента выглядела одинаковой.
@keyframes benefitGlow {
  0%, 100% {
    box-shadow: 0 1px 3px rgb(0 0 0 / 0.05);
  }
  50% {
    box-shadow: 0 1px 3px rgb(0 0 0 / 0.05), 0 0 18px rgb(var(--benefit-tone) / 0.25);
  }
}

// ============================================
// ♿ ACCESSIBILITY - Доступность
// ============================================

// Сенсорные устройства
@media (hover: none) and (pointer: coarse) {
  .info-card {
    &:hover {
      transform: none;
    }

    &:hover :deep(.goal-ring__badge) {
      transform: none;
    }
  }
}

// Высокий контраст
@media (prefers-contrast: high) {
  .info-card {
    @apply border-2;
  }
}

// Уменьшенная анимация
@media (prefers-reduced-motion: reduce) {
  .info-card {
    animation: none !important;
    transition: none;
  }

  .info-card:hover,
  .info-card:hover :deep(.goal-ring__badge) {
    transform: none;
  }
}
</style>
